/**
 * SketchesTab — Container for Schițe (sketches/floor plans) feature.
 *
 * Project list (localStorage), drawing canvas, toolbar with all tools,
 * unit selector, snap/grid toggle, undo/redo, save, export PNG/PDF.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Pencil, Plus, Trash2, Save, Download, Image as ImageIcon, FileText,
  MousePointer2, Move, Minus, Square, Circle as CircleIcon, DoorOpen,
  RectangleHorizontal, Type, Ruler, Eraser, Undo2, Redo2, Grid3x3, Magnet,
  ZoomIn, ZoomOut, BoxSelect
} from "lucide-react";
import SketchCanvas from "./SketchCanvas";
import MapMeasurementsPanel from "./MapMeasurementsPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import jsPDF from "jspdf";

const STORAGE_KEY = (companyId) => `etesa_sketches_${companyId || "default"}`;

const TOOLS = [
  { id: "select", label: "Selectează", icon: MousePointer2 },
  { id: "pan", label: "Mută canvas", icon: Move },
  { id: "pen", label: "Desen liber", icon: Pencil },
  { id: "line", label: "Linie", icon: Minus },
  { id: "wall", label: "Perete", icon: Minus },
  { id: "rect", label: "Cameră (dreptunghi)", icon: Square },
  { id: "circle", label: "Cerc", icon: CircleIcon },
  { id: "door", label: "Ușă", icon: DoorOpen },
  { id: "window", label: "Fereastră", icon: RectangleHorizontal },
  { id: "text", label: "Text", icon: Type },
  { id: "dimension", label: "Cotă/Dimensiune", icon: Ruler },
];

const emptyProject = (name) => ({
  id: `pj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: name || "Schiță nouă",
  unit: "m",
  shapes: [],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});

const loadProjects = (companyId) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(companyId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};
const saveProjects = (companyId, list) => {
  try { localStorage.setItem(STORAGE_KEY(companyId), JSON.stringify(list)); } catch (e) { console.error(e); }
};

export default function SketchesTab({ companyId }) {
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newName, setNewName] = useState("");

  // Canvas state for the open project
  const [shapes, setShapes] = useState([]);
  const [unit, setUnit] = useState("m");
  const [tool, setTool] = useState("select");
  const [snap, setSnap] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);

  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 540 });

  useEffect(() => {
    setProjects(loadProjects(companyId));
  }, [companyId]);

  // Resize observer for canvas
  useEffect(() => {
    if (!wrapRef.current) return;
    const update = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setSize({ w: Math.floor(r.width), h: Math.max(420, Math.floor(r.height)) });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [activeId]);

  const active = useMemo(() => projects.find((p) => p.id === activeId), [projects, activeId]);

  // Load shapes when opening a project (only when activeId changes, NOT when
  // projects array identity changes — otherwise Save wipes Undo history).
  const prevActiveIdRef = useRef(null);
  useEffect(() => {
    if (active && prevActiveIdRef.current !== activeId) {
      prevActiveIdRef.current = activeId;
      setShapes(active.shapes || []);
      setUnit(active.unit || "m");
      setHistory([]);
      setFuture([]);
      setSelectedId(null);
      setTool("select");
    }
    if (!active) {
      prevActiveIdRef.current = null;
    }
  }, [active, activeId]);

  const pushHistory = useCallback((prevShapes) => {
    setHistory((h) => [...h.slice(-49), prevShapes]);
    setFuture([]);
  }, []);

  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setFuture((f) => [shapes, ...f]);
    setHistory((h) => h.slice(0, -1));
    setShapes(prev);
  };
  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setHistory((h) => [...h, shapes]);
    setFuture((f) => f.slice(1));
    setShapes(next);
  };

  const handleNewProject = () => {
    const p = emptyProject(newName.trim() || "Schiță nouă");
    const updated = [p, ...projects];
    setProjects(updated);
    saveProjects(companyId, updated);
    setActiveId(p.id);
    setShowNewDialog(false);
    setNewName("");
    toast.success("Schiță creată");
  };

  const handleSave = () => {
    if (!active) return;
    const updated = projects.map((p) =>
      p.id === activeId ? { ...p, shapes, unit, updated_at: new Date().toISOString() } : p
    );
    setProjects(updated);
    saveProjects(companyId, updated);
    toast.success("Schiță salvată local");
  };

  const handleDeleteProject = (id) => {
    if (!window.confirm("Ștergi această schiță? Acțiunea nu poate fi anulată.")) return;
    const updated = projects.filter((p) => p.id !== id);
    setProjects(updated);
    saveProjects(companyId, updated);
    if (id === activeId) setActiveId(null);
    toast.success("Schiță ștearsă");
  };

  const handleClearCanvas = () => {
    if (!shapes.length) return;
    if (!window.confirm("Golești complet planșa? (poți face Undo)")) return;
    pushHistory(shapes);
    setShapes([]);
    setSelectedId(null);
  };

  const handleDeleteSelected = () => {
    if (!selectedId) return;
    pushHistory(shapes);
    setShapes(shapes.filter((s) => s.id !== selectedId));
    setSelectedId(null);
  };

  // Îndreaptă forma selectată (quad → dreptunghi axă-aliniat).
  // Folosește dimensiunile medii ale laturilor opuse, centrată pe centroidul actual.
  const handleStraightenShape = () => {
    if (!selectedId) return;
    const sel = shapes.find((s) => s.id === selectedId);
    if (!sel || (sel.type !== "quad" && sel.type !== "rect")) {
      toast.info("Selectează un dreptunghi sau quad pentru a-l îndrepta");
      return;
    }
    if (sel.type === "rect") {
      toast.info("Forma este deja dreptunghi");
      return;
    }
    const c = sel.corners;
    const top = Math.hypot(c[1].x - c[0].x, c[1].y - c[0].y);
    const right = Math.hypot(c[2].x - c[1].x, c[2].y - c[1].y);
    const bottom = Math.hypot(c[3].x - c[2].x, c[3].y - c[2].y);
    const left = Math.hypot(c[0].x - c[3].x, c[0].y - c[3].y);
    const avgW = (top + bottom) / 2;
    const avgH = (left + right) / 2;
    const cx = (c[0].x + c[1].x + c[2].x + c[3].x) / 4;
    const cy = (c[0].y + c[1].y + c[2].y + c[3].y) / 4;
    pushHistory(shapes);
    setShapes((arr) => arr.map((s) => s.id === selectedId
      ? { ...s, type: "rect", x: cx - avgW / 2, y: cy - avgH / 2, width: avgW, height: avgH, corners: undefined }
      : s
    ));
    toast.success("Formă îndreptată — dreptunghi cu unghiuri 90°");
  };

  // Export helpers — use the canvas ref directly
  const getStageDataURL = () => {
    if (canvasRef.current?.toDataURL) {
      return canvasRef.current.toDataURL("image/png", 0.95);
    }
    return null;
  };

  const handleExportPNG = () => {
    const url = getStageDataURL();
    if (!url) { toast.error("Canvas nedisponibil"); return; }
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(active?.name || "schita").replace(/[^a-zA-Z0-9ăîâșțĂÎÂȘȚ\s\-\.]/g, "")}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("PNG descărcat");
  };

  const handleExportPDF = () => {
    const url = getStageDataURL();
    if (!url) { toast.error("Canvas nedisponibil"); return; }
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    // Fit image within page with 10mm margin
    const margin = 10;
    const maxW = pageW - 2 * margin;
    const maxH = pageH - 2 * margin - 14;
    pdf.setFontSize(14);
    pdf.text(active?.name || "Schiță", margin, margin + 6);
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text(`Generat e-Tesa · ${new Date().toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, margin + 11);
    pdf.setTextColor(0);
    // Use offscreen img to read intrinsic size
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      let w = maxW, h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      pdf.addImage(url, "PNG", margin + (maxW - w) / 2, margin + 14, w, h);
      pdf.save(`${(active?.name || "schita").replace(/[^a-zA-Z0-9ăîâșțĂÎÂȘȚ\s\-\.]/g, "")}.pdf`);
      toast.success("PDF descărcat");
    };
    img.src = url;
  };

  // Project list view
  if (!active) {
    return (
      <Tabs defaultValue="measurements" className="space-y-4">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="measurements" data-testid="sketches-tab-measurements">
            Măsurători (hartă / plan)
          </TabsTrigger>
          <TabsTrigger value="freehand" data-testid="sketches-tab-freehand">
            Schițe libere (canvas)
          </TabsTrigger>
        </TabsList>
        <TabsContent value="measurements" className="mt-0">
          <MapMeasurementsPanel companyId={companyId} />
        </TabsContent>
        <TabsContent value="freehand" className="mt-0">
          <Card className="border-0 shadow-md" data-testid="sketches-tab">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-teal-600" />
                Schițe & Planuri
                <Badge className="bg-teal-100 text-teal-700 border-0">Local</Badge>
              </CardTitle>
              <CardDescription>
                Desenează planuri 2D, schițe libere și etichete direct în browser.
                Proiectele se salvează local (fără cont), suport mouse + touch.
              </CardDescription>
            </div>
            <Button onClick={() => setShowNewDialog(true)} data-testid="sketch-new-btn"
              className="bg-teal-600 hover:bg-teal-700 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Schiță nouă
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-12 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
              <Pencil className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Nu ai nicio schiță salvată. Apasă „Schiță nouă" ca să începi.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map((p) => (
                <div key={p.id}
                  data-testid={`sketch-project-${p.id}`}
                  className="group border border-slate-200 rounded-lg p-4 hover:border-teal-300 hover:shadow-md transition cursor-pointer bg-white"
                  onClick={() => setActiveId(p.id)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">{p.name}</h3>
                      <p className="text-xs text-slate-500 mt-1">
                        {(p.shapes || []).length} elemente · {p.unit}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Modificat: {new Date(p.updated_at).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); handleDeleteProject(p.id); }}
                      data-testid={`sketch-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>

        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Schiță nouă</DialogTitle>
              <DialogDescription>
                Alege un nume pentru noua schiță. Proiectul se va salva local în browser.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                autoFocus
                placeholder="Nume schiță (ex: Plan parter Apartament 12)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleNewProject(); }}
                data-testid="sketch-new-name-input"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewDialog(false)}>Anulează</Button>
                <Button onClick={handleNewProject} className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="sketch-new-confirm-btn">
                  Creează
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </Card>
        </TabsContent>
      </Tabs>
    );
  }

  // Editor view
  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid="sketches-editor">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setActiveId(null)} data-testid="sketch-back-btn">
            ← Înapoi la listă
          </Button>
          <div className="font-semibold text-slate-800 truncate flex-1 min-w-0">
            <Pencil className="w-4 h-4 inline mr-1.5 text-teal-600" />
            {active.name}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="w-20 h-9" data-testid="sketch-unit-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mm">mm</SelectItem>
                <SelectItem value="cm">cm</SelectItem>
                <SelectItem value="m">m</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleSave} data-testid="sketch-save-btn">
              <Save className="w-4 h-4 mr-1" /> Salvează
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPNG} data-testid="sketch-export-png">
              <ImageIcon className="w-4 h-4 mr-1" /> PNG
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="sketch-export-pdf">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="px-3 py-2 border-y border-slate-200 bg-slate-50 flex items-center gap-1 flex-wrap">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const isActive = tool === t.id;
            return (
              <Button key={t.id}
                size="sm"
                variant={isActive ? "default" : "ghost"}
                title={t.label}
                onClick={() => setTool(t.id)}
                data-testid={`sketch-tool-${t.id}`}
                className={isActive ? "bg-teal-600 hover:bg-teal-700 text-white h-9 px-2" : "h-9 px-2 text-slate-700"}
              >
                <Icon className="w-4 h-4" />
              </Button>
            );
          })}
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <Button size="sm" variant="ghost" title="Undo" onClick={undo} disabled={!history.length}
            data-testid="sketch-undo-btn" className="h-9 px-2"><Undo2 className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" title="Redo" onClick={redo} disabled={!future.length}
            data-testid="sketch-redo-btn" className="h-9 px-2"><Redo2 className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" title="Șterge selecție" onClick={handleDeleteSelected}
            disabled={!selectedId} data-testid="sketch-delete-selected-btn" className="h-9 px-2">
            <Eraser className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" title="Golește planșa" onClick={handleClearCanvas}
            data-testid="sketch-clear-btn" className="h-9 px-2 text-red-500 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
          </Button>
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <Button size="sm" variant="ghost" title="Îndreaptă forma (quad → dreptunghi cu unghiuri 90°)"
            onClick={handleStraightenShape} disabled={!selectedId}
            data-testid="sketch-straighten-btn"
            className="h-9 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50">
            <BoxSelect className="w-4 h-4" />
          </Button>
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <Button size="sm" variant={snap ? "default" : "ghost"} title="Snap-to-grid"
            onClick={() => setSnap((v) => !v)} data-testid="sketch-snap-btn"
            className={snap ? "bg-slate-700 text-white h-9 px-2" : "h-9 px-2"}>
            <Magnet className="w-4 h-4" />
          </Button>
          <Button size="sm" variant={showGrid ? "default" : "ghost"} title="Afișează grilă"
            onClick={() => setShowGrid((v) => !v)} data-testid="sketch-grid-btn"
            className={showGrid ? "bg-slate-700 text-white h-9 px-2" : "h-9 px-2"}>
            <Grid3x3 className="w-4 h-4" />
          </Button>
          <div className="ml-auto text-xs text-slate-500 hidden sm:block">
            Scroll = zoom · Click pe colț = mută colț · Click pe latură = setează dimensiune
          </div>
        </div>

        {/* Canvas */}
        <div ref={wrapRef} className="relative bg-white" style={{ height: "65vh", minHeight: 480, touchAction: "none" }}>
          <SketchCanvas
            ref={canvasRef}
            width={size.w}
            height={size.h}
            shapes={shapes}
            setShapes={setShapes}
            tool={tool}
            setTool={setTool}
            unit={unit}
            snap={snap}
            showGrid={showGrid}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            pushHistory={pushHistory}
          />
        </div>
      </CardContent>
    </Card>
  );
}
